import type { BonusGoal, LevelDef, Passenger } from '@full-elevator/sim';
import { bonusPayFor, costOfLoss, createInitialState, SERVICE } from '@full-elevator/sim';

/**
 * What a shift asks, said before it starts (ADR-0037).
 *
 * Forty of the shifts grade on the score and the rest on time, and five
 * different objectives decide when a shift is won — none of which a player saw
 * before playing. Every shift felt like the same job with a different number
 * of people. Candy Crush opens each level with its goal; so does a shift now.
 *
 * Words only, read off the level: nothing here decides what counts.
 */
export interface Goal {
  /** Big, at a glance: "Everyone home", "13 of 16". */
  readonly headline: string;
  /** The sentence under it. */
  readonly line: string;
  /** Three stars and two, in the level's own units. */
  readonly three: string;
  readonly two: string;
  /**
   * The shift's bonus goal and what it pays the first time, or null (ADR-0040),
   * and a line under it that says what its unit is, where the card does not.
   */
  readonly bonus: {
    readonly text: string;
    readonly coins: number;
    readonly note: string | null;
  } | null;
  /** How points are earned, on a shift graded on them; null otherwise. */
  readonly scoring: string | null;
}

/**
 * How the points rule names someone who costs more to lose than anyone else.
 * A character whose loss costs more and has no name here fails a test.
 */
export const DEARER: Readonly<Record<string, string>> = {
  courier: 'a courier',
  vip: 'a VIP',
  roomService: 'room service',
  commuter: 'someone with a train',
  shuttleGuest: 'someone for the shuttle',
  breakfastGuest: 'someone for breakfast',
  newlywed: 'the couple',
  ceo: 'the chief executive',
  investor: 'the investor',
};

/**
 * The most the sim would charge for losing this person: walking off (or being
 * turned away, which costs the same), or their deadline passing if they have one.
 */
export function dearestLoss(passenger: Passenger): number {
  const walked = costOfLoss(passenger, 'gave-up');
  return passenger.deadlineMs === null
    ? walked
    : Math.max(walked, costOfLoss(passenger, 'deadline'));
}

/**
 * Who on this shift costs more to lose than an ordinary person, and how much:
 * read off the shift's own people and priced by the sim. A crew is left out:
 * losing one ends the shift, which their card says.
 */
export function dearerLosses(level: LevelDef): { readonly who: string; readonly cost: number }[] {
  const dearest = new Map<string, number>();
  for (const passenger of createInitialState(level, { mode: 'dispatch' }).passengers) {
    if (passenger.critical) continue;
    const cost = dearestLoss(passenger);
    if (cost <= SERVICE.abandonPenalty) continue;
    dearest.set(passenger.archetype, Math.max(cost, dearest.get(passenger.archetype) ?? 0));
  }
  return [...dearest]
    .map(([archetype, cost]) => ({ who: DEARER[archetype] ?? '', cost }))
    .sort((a, b) => a.cost - b.cost || a.who.localeCompare(b.who));
}

/**
 * The points rule, from `SERVICE` in the sim. Points grade most shifts, and
 * the card used to say "a quick trip scores more", which is wrong: only the
 * wait for the lift costs, and the ride is free (studio review, 2026-10-22).
 * "Less 4" read as "not as much", and "losing someone costs 120" was wrong
 * for a courier or a VIP (writer review, 2026-10-26), so it names whoever on
 * this shift costs more.
 */
export function pointsRule(level: LevelDef): string {
  const dearer = dearerLosses(level);
  // One price once: "−150 for a courier or room service".
  const byCost = new Map<number, string[]>();
  for (const { who, cost } of dearer) byCost.set(cost, [...(byCost.get(cost) ?? []), who]);
  const named = [...byCost].map(([cost, who]) => `−${cost} for ${who.join(' or ')}`);
  const lost =
    dearer.length === 0
      ? `−${SERVICE.abandonPenalty}`
      : dearer.some((d) => d.who === '')
        ? `−${SERVICE.abandonPenalty} or more`
        : `−${SERVICE.abandonPenalty}, or ${listOf(named)}`;
  return (
    `Each person you take: +${SERVICE.deliveryBase} points. ` +
    `Each second they wait for the lift: −${SERVICE.waitPenaltyPerSecond}. ` +
    `The ride is free. Each person lost: ${lost}.`
  );
}

/** "a", "a and b", "a, b and c". */
/** The hotel's departures (ADR-0051): a courier is not all who has a bar there. */
const HOTEL_DEPARTURES = new Set(['shuttleGuest', 'breakfastGuest', 'newlywed']);

function listOf(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;
}

/**
 * A bonus goal in words. Who has a deadline depends on the shift: couriers in
 * the office, commuters running for a train in the station.
 */
export function bonusText(bonus: BonusGoal, level: LevelDef): string {
  switch (bonus.kind) {
    case 'noneLost':
      return 'Lose nobody';
    case 'couriersOnTime':
      return level.spawns.some((s) => s.archetype === 'commuter')
        ? 'Everyone makes their train'
        : level.spawns.some((s) => HOTEL_DEPARTURES.has(s.archetype ?? ''))
          ? 'Everyone with a bar on time'
          : 'Every courier on time';
    case 'allVips':
      return 'Every VIP home';
    case 'withinTrips':
      return `Done in ${bonus.trips} trips or fewer`;
  }
}

/**
 * What a trip is, said once. A trip bonus graded 8 of office 1–15 and hotel 1–3,
 * and only a puzzle's card said what a trip was, when those shifts had no puzzle
 * (writer review, 2026-10-26). A trip is each time a car sets off (`score.trips`).
 */
export const TRIP_NOTE = 'A trip: each time the lift sets off.';

function bonusOf(level: LevelDef): Goal['bonus'] {
  if (!level.bonus) return null;
  // A puzzle's own line says it already.
  const note =
    level.bonus.kind === 'withinTrips' && level.objective.kind !== 'withinTrips' ? TRIP_NOTE : null;
  return { text: bonusText(level.bonus, level), coins: bonusPayFor(true, level.payout), note };
}

/** Minutes and seconds, as the clock in the HUD shows them. */
export function clockText(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return minutes > 0 ? `${minutes}:${String(seconds).padStart(2, '0')}` : `${seconds}s`;
}

export function goalOf(level: LevelDef): Goal {
  const { objective, stars } = level;
  const people = level.spawns.length;
  const byTime = (stars.metric ?? 'time') === 'time';
  const measure = (value: number) =>
    stars.metric === 'trips'
      ? `in ${value} trips`
      : byTime
        ? `under ${clockText(value)}`
        : `${value} points`;
  const three = measure(stars.three);
  const two = measure(stars.two);
  const scoring = stars.metric === 'service' ? pointsRule(level) : null;

  switch (objective.kind) {
    case 'deliverAll':
      return {
        headline: 'Everyone home',
        line: byTime
          ? 'Take every one of them where they want to go. The faster, the more stars.'
          : 'Take every one of them where they want to go, and lose nobody.',
        three,
        two,
        bonus: bonusOf(level),
        scoring,
      };
    case 'deliverN':
      return {
        headline: `${objective.count} of ${people}`,
        // The shift ends the moment the quota is home, which surprised the owner
        // twice with people still waiting (PLAYTEST_LOG, 2026-10-28).
        line: `Take ${objective.count} of the ${people} people where they want to go. The shift ends when ${objective.count} are home.`,
        three,
        two,
        bonus: bonusOf(level),
        scoring,
      };
    case 'serviceAbove':
      return {
        headline: `${objective.count} people · ${objective.minService} points`,
        line: `Take at least ${objective.count} people, and finish with ${objective.minService} points or more. Then the shift ends.`,
        three,
        two,
        bonus: bonusOf(level),
        scoring,
      };
    case 'withinTrips':
      return {
        headline: `Everyone home in ${objective.trips} trips`,
        // Played on the first lift (ADR-0049), so the card says the parts stay behind.
        line: 'No clock, and your parts stay in the garage. Each time the lift sets off is one trip: plan them.',
        three,
        two,
        bonus: bonusOf(level),
        scoring,
      };
    case 'survive':
      return {
        headline: `Hold out ${clockText(objective.untilMs)}`,
        line:
          objective.maxLost === 0
            ? 'Keep the lift running until the clock reaches 0:00, and lose nobody.'
            : `Keep the lift running until the clock reaches 0:00. Lose more than ${objective.maxLost} and it is over.`,
        three,
        two,
        bonus: bonusOf(level),
        scoring,
      };
  }
}

/** An icon on the goal card (PLAYTEST_LOG 2026-10-29): one fact, one icon. */
export type FigureIcon = 'home' | 'clock' | 'trip' | 'lost' | 'person' | 'star';

export interface Figure {
  readonly icon: FigureIcon | null;
  readonly value: string;
  /** Small after the number: "/ 4", or a unit word. */
  readonly of?: string;
  /** A few words under it, so the figure reads without a tap (PLAYTEST_LOG 2026-10-30). */
  readonly label?: string;
  /** What it means, said at a tap. */
  readonly tip?: string;
}

/** The goal card as icons and numbers; the sentences go behind its "i". */
export interface GoalFigures {
  /** What wins the shift, big. */
  readonly hero: readonly Figure[];
  /** The marks for three stars and two, in the unit the icon shows. */
  readonly stars: {
    readonly icon: 'clock' | 'trip' | null;
    readonly three: string;
    readonly two: string;
  };
  /** The points rule, on a shift graded on points. */
  readonly scoring: readonly (Figure & { readonly good: boolean })[];
  readonly bonus: {
    readonly icon: 'trip' | null;
    readonly text: string;
    readonly coins: number;
    /** The bonus in a sentence, said at a tap. */
    readonly tip: readonly string[];
  } | null;
  /** Everything the card no longer says on its face. */
  readonly more: readonly string[];
}

function heroOf(level: LevelDef): Figure[] {
  const objective = level.objective;
  const people = level.spawns.length;
  const everyone: Figure = {
    icon: 'home',
    value: String(people),
    of: `/ ${people}`,
    label: 'people home',
    tip: `Take all ${people} to the floor on their ticket.`,
  };
  switch (objective.kind) {
    case 'deliverAll':
      return [everyone];
    case 'deliverN':
      return [
        {
          icon: 'home',
          value: String(objective.count),
          of: `/ ${people}`,
          label: 'people home',
          tip: `Take ${objective.count} of the ${people} to the floor on their ticket.`,
        },
      ];
    case 'serviceAbove':
      return [
        {
          icon: 'home',
          value: String(objective.count),
          label: 'people home',
          tip: `Take at least ${objective.count} to the floor on their ticket.`,
        },
        {
          icon: 'star',
          value: String(objective.minService),
          label: 'points or more',
          tip: `And end with at least ${objective.minService} points. Each person home scores; waiting and losing people cost.`,
        },
      ];
    case 'withinTrips':
      return [
        everyone,
        {
          icon: 'trip',
          value: String(objective.trips),
          label: 'trips at most',
          tip: `Every time the lift sets off is a trip. You have ${objective.trips}: plan who rides together.`,
        },
      ];
    case 'survive':
      return [
        {
          icon: 'clock',
          value: clockText(objective.untilMs),
          label: 'keep it running',
          tip: `Keep the building moving for ${clockText(objective.untilMs)}.`,
        },
        {
          icon: 'lost',
          value: objective.maxLost === 0 ? '0' : `≤ ${objective.maxLost}`,
          label: 'people lost',
          tip:
            objective.maxLost === 0
              ? 'Lose nobody: someone who gives up waiting ends the shift.'
              : `Lose ${objective.maxLost} or fewer. One more and the shift is over.`,
        },
      ];
  }
}

function bonusTip(bonus: { text: string; coins: number; note: string | null }): string[] {
  return [`${bonus.text}: ${bonus.coins} coins more.`, bonus.note ?? ''].filter((l) => l !== '');
}

export function goalFigures(level: LevelDef): GoalFigures {
  const goal = goalOf(level);
  const { stars } = level;
  const unit = stars.metric ?? 'time';
  const mark = (value: number) => (unit === 'time' ? clockText(value) : String(value));
  const dearer = dearerLosses(level).length > 0;
  const scoring: (Figure & { good: boolean })[] =
    unit === 'service'
      ? [
          {
            icon: 'person',
            value: `+${SERVICE.deliveryBase}`,
            good: true,
            label: 'each home',
            tip: `Every person you take home scores ${SERVICE.deliveryBase} points.`,
          },
          {
            icon: 'clock',
            value: `−${SERVICE.waitPenaltyPerSecond}/s`,
            good: false,
            label: 'while waiting',
            tip: `Each person waiting costs ${SERVICE.waitPenaltyPerSecond} points every second, until the lift picks them up.`,
          },
          {
            icon: 'lost',
            value: `−${SERVICE.abandonPenalty}${dearer ? '+' : ''}`,
            good: false,
            label: 'each lost',
            tip: dearer
              ? `Someone who gives up costs ${SERVICE.abandonPenalty} points, and some here cost more.`
              : `Someone who gives up costs ${SERVICE.abandonPenalty} points.`,
          },
        ]
      : [];
  const bonus = level.bonus;
  return {
    hero: heroOf(level),
    stars: {
      icon: unit === 'time' ? 'clock' : unit === 'trips' ? 'trip' : null,
      three: mark(stars.three),
      two: mark(stars.two),
    },
    scoring,
    bonus:
      bonus && goal.bonus
        ? bonus.kind === 'withinTrips'
          ? {
              icon: 'trip',
              text: `≤ ${bonus.trips}`,
              coins: goal.bonus.coins,
              tip: bonusTip(goal.bonus),
            }
          : {
              icon: null,
              text: goal.bonus.text,
              coins: goal.bonus.coins,
              tip: bonusTip(goal.bonus),
            }
        : null,
    more: [
      goal.line,
      goal.scoring ?? '',
      goal.bonus ? `Bonus: ${goal.bonus.text}.` : '',
      goal.bonus?.note ?? '',
    ].filter((line) => line !== ''),
  };
}
